// Painel de Autos de Infração (spec 2026-10-15). Somente leitura; filtros de
// período e posto na URL. A definição dos números mora no backend
// (services/paineis/autosInfracao.js + montaPainelAutos.js); esta tela só desenha.
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBBtn, MDBSpinner, MDBIcon } from 'mdbreact';

import NavBar from '../../../components/NavBar';
import api from '../../../services/api';
import {
  Colunas, KpiTile, ListaBarras, NotaMetodologica,
  formataNumero, formataPct, formataReais, formataReaisCurto, formataReaisCheio, formataVariacao, formataPontos, formataDataHora,
  formataFracao,
} from '../../../components/Painel';
import {
  filtrosDoPainel, queryDoPainel, paramsDoPainel, rotuloMes, rotuloPeriodo, linkLista, notaSemAutos,
  rotuloInfracao, limpaCampo, opcoesDePosto, rotuloJanela, ordenaPostos, notaConcentracao,
} from './filtrosPainel';

const NOTA = [
  {
    icone: 'database',
    titulo: 'Fonte',
    texto: 'Autos de infração da Receita Estadual (SEFAZ), importados por inteiro todo início de mês. A data da última carga aparece no topo: o painel não é ao vivo.',
  },
  {
    icone: 'calendar',
    titulo: 'Período',
    texto: 'Mês da data de lavratura. O padrão são os últimos 12 meses fechados. Autos sem data de lavratura ficam fora de qualquer período (a quantidade aparece no rodapé).',
  },
  {
    icone: 'money',
    titulo: 'Lavrado',
    texto: 'Soma do valor total dos autos (imposto + multa + multa formal) lavrados no período.',
  },
  {
    icone: 'check',
    titulo: 'Liquidado é retrato, não fluxo',
    texto: (
      <>
        Conta como liquidado o auto que <strong>hoje</strong> consta como &quot;Liquidado&quot; na Receita, qualquer
        que seja o mês em que foi lavrado. Por isso os meses recentes sempre mostram pouco liquidado: o pagamento
        ainda não aconteceu. Parcelado, em cobrança ou em dívida ativa não contam.
      </>
    ),
  },
  {
    icone: 'hourglass-half',
    titulo: 'Sem acompanhamento',
    texto: (
      <>
        Auto que a Receita ainda não processou: está na base, mas sem situação de cobrança. Concentra-se nos meses
        recentes, onde pode ser a maioria dos autos — por isso o liquidado desses meses é baixo, e a situação
        deles ainda vai mudar nas próximas cargas.
      </>
    ),
  },
  {
    icone: 'percent',
    titulo: 'Taxa de liquidação',
    texto: 'Liquidado ÷ lavrado, em valor. A comparação com o ano anterior é em pontos percentuais (p.p.).',
  },
  {
    icone: 'map-marker',
    titulo: 'Posto',
    texto: 'Vem do RF de lavratura: 9912 Nhangapi, 9919 Levy Gasparian, 9913 Mato Verde, 9914 Timbó, 9923 Angra dos Reis, 14 Auditoria Especializada. Os demais (e RF em branco) são "Outros" — e entram nos totais. Os dashboards do Metabase tiram "Outros" no filtro padrão: para os números baterem, marque "Outros" lá.',
  },
  {
    icone: 'car',
    titulo: 'Abordagens e ocorrências (SGO)',
    texto: (
      <>
        Do módulo de Gestão de Ocorrências, com a regra de contagem dos relatórios da coordenação (abordagens e
        ocorrências ativas, pela data da abordagem; ocorrência sem item apreendido conta, com vários itens conta
        uma vez), nos 5 postos fiscais, pela lotação de quem registrou. Abordagens de outras gerências (ex.: Núcleo
        de Operação Volante) ficam fora: não têm RF da Receita para comparar. Auditoria Especializada e
        &quot;Outros&quot; não são posto do SIG (&quot;—&quot;). <strong>Autos / 100 abordagens</strong> compara bases
        diferentes — autos da Receita (pelo RF) e abordagens do SIG (pela lotação) — e serve para comparar postos
        entre si, não como taxa de conversão: um posto pode ter mais autos que abordagens. Posto que aborda mas
        não recebe nenhum auto da Receita no período aparece com &quot;—*&quot;.
      </>
    ),
  },
  {
    icone: 'adjust',
    titulo: 'Mês incompleto',
    texto: 'Colunas hachuradas são meses a partir da última carga: ainda não estão completos na base.',
  },
];

// A coluna inteira é o lavrado e o azul é a parte dele já liquidada — o cinza
// visível é lavrado − liquidado, então "cinza = lavrado" induzia a somar os dois.
const LEGENDA = [['var(--painel-b)', 'lavrado (coluna inteira)'], ['var(--painel-a)', 'liquidado hoje (parte azul)']];

const Legenda = () => (
  <div className="painel-legenda mb-2">
    {LEGENDA.map(([cor, texto]) => (
      <span key={texto}><span className="painel-legenda__cor" style={{ background: cor }} />{texto}</span>
    ))}
    <span className="text-muted">valores em R$</span>
  </div>
);

const pct = formataFracao;
const umaCasa = (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
// `== null` (e não `===`): contra um backend sem os campos do SGO eles chegam
// undefined, e umaCasa(undefined) derrubava a tela (#395).
const contagem = (v) => (v == null ? '—' : formataNumero(v));
// Mês corrente, teto dos campos de mês: período futuro dava "▼ 100%" (#395).
const MES_ATUAL = new Date().toISOString().slice(0, 7);
const linkSemCaixaAlta = { fontSize: 'inherit', textTransform: 'none', boxShadow: 'none' };

export default function PainelAutosInfracao() {
  const history = useHistory();
  const location = useLocation();
  const filtros = filtrosDoPainel(location.search);
  const [rascunho, setRascunho] = useState(filtros);
  const [dados, setDados] = useState(null);
  // Última lista de postos que veio: um 400 zera `dados`, não o select.
  const [postos, setPostos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null); // { tipo: 'sem-acesso' | 'invalido' | 'falha', texto }
  const [ordemInfracao, setOrdemInfracao] = useState('por_valor');
  // Situação por nº ou por R$ (#398): por nº, Liquidado vinha no topo com 10,9% do valor.
  const [statusPorValor, setStatusPorValor] = useState(false);

  useEffect(() => {
    // URL mudou (Aplicar, voltar do navegador, link compartilhado) ou "Atualizar".
    setRascunho(filtros);
    let vivo = true;
    setCarregando(true);
    setErro(null);
    api.get('/paineis/autos-infracao', { params: paramsDoPainel(filtros) })
      .then(({ data }) => { if (vivo) { setDados(data); setPostos(data.postos); } })
      .catch((e) => {
        if (!vivo) return;
        const status = e && e.response && e.response.status;
        setDados(null);
        if (status === 403) setErro({ tipo: 'sem-acesso' });
        else if (status === 400) {
          setErro({ tipo: 'invalido', texto: e.response.data.erro });
          // Limpa só o campo que o backend apontou: senão o "Aplicar" reenviava o
          // mesmo valor inválido (#395).
          setRascunho(limpaCampo(filtros, e.response.data.campo));
        }
        else setErro({ tipo: 'falha' });
      })
      .finally(() => vivo && setCarregando(false));
    return () => { vivo = false; };
    // location.search/state são a dependência real; `filtros` deriva deles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state]);

  const aplicar = () => history.push({ pathname: location.pathname, search: queryDoPainel(rascunho) });
  const atualizar = () =>
    history.replace({ pathname: location.pathname, search: location.search, state: { t: Date.now() } });
  const irPara = (link) => { if (link) history.push(link); };

  const filtrosForm = (
    <div className="d-flex flex-wrap align-items-end mb-3" style={{ gap: '.75rem' }}>
      <label className="mb-0" style={{ fontSize: '.85rem' }}>
        De
        <input
          type="month"
          min="2010-01"
          max={MES_ATUAL}
          className="form-control form-control-sm"
          value={rascunho.de || (dados ? dados.periodo.de : '')}
          onChange={(e) => setRascunho({ ...rascunho, de: e.target.value })}
        />
      </label>
      <label className="mb-0" style={{ fontSize: '.85rem' }}>
        Até
        <input
          type="month"
          min="2010-01"
          max={MES_ATUAL}
          className="form-control form-control-sm"
          value={rascunho.ate || (dados ? dados.periodo.ate : '')}
          onChange={(e) => setRascunho({ ...rascunho, ate: e.target.value })}
        />
      </label>
      <label className="mb-0" style={{ fontSize: '.85rem' }}>
        Posto
        <select
          className="browser-default custom-select custom-select-sm"
          value={rascunho.posto}
          onChange={(e) => setRascunho({ ...rascunho, posto: e.target.value })}
        >
          <option value="">Todos</option>
          {opcoesDePosto(postos, rascunho.posto).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </label>
      <button type="button" className="painel-acao" onClick={aplicar}>
        <MDBIcon icon="filter" />
        Aplicar
      </button>
    </div>
  );

  const corpo = () => {
    if (carregando && !dados) return <div className="text-center my-5"><MDBSpinner /></div>;
    if (erro && erro.tipo === 'sem-acesso') return <p className="my-5 text-center">Você não tem acesso a este painel.</p>;
    if (erro && erro.tipo === 'invalido') return <p className="my-5 text-center">{erro.texto}</p>;
    if (erro) {
      return <div className="text-center my-5"><MDBBtn color="primary" onClick={atualizar}>Tentar de novo</MDBBtn></div>;
    }

    const { cards: c, comparacao: cmp, periodo, anterior, posto } = dados;
    const vs = `vs. ${rotuloPeriodo(anterior)}`;
    const porValor = ordemInfracao === 'por_valor';
    const infracoes = dados.por_infracao[ordemInfracao];
    const totalPostos = dados.por_posto.reduce(
      (s, p) => ({ autos: s.autos + p.autos, lavrado: s.lavrado + p.lavrado, liquidado: s.liquidado + p.liquidado }),
      { autos: 0, lavrado: 0, liquidado: 0 },
    );
    // Totais do SGO: só postos com gerência; a razão só sobre os postos que têm razão
    // (numerador e denominador do mesmo conjunto).
    const comSgo = dados.por_posto.filter((p) => p.abordagens != null);
    const comRazao = comSgo.filter((p) => p.autos_por_100_abordagens != null);
    const somaDe = (ls, k) => ls.reduce((s, p) => s + p[k], 0);
    const abordRazao = somaDe(comRazao, 'abordagens');
    const notaSemAutosReceita = notaSemAutos(
      dados.por_posto.filter((p) => p.sem_autos_receita).map((p) => p.posto),
      dados.meses_incompletos,
    );
    const avisosSgo = dados.avisos_sgo || [];
    const concentracao = notaConcentracao(dados.por_infracao);
    const alterna = (rotulo, emReais, aoMudar) => (
      <div className="painel-acoes" role="group" aria-label={rotulo}>
        {[[true, 'por R$'], [false, 'por nº']].map(([v, txt]) => (
          <button
            key={txt}
            type="button"
            className={`painel-acao${emReais === v ? ' active' : ''}`}
            aria-pressed={emReais === v}
            onClick={() => aoMudar(v)}
          >
            {txt}
          </button>
        ))}
      </div>
    );
    const kpis = [
      { valor: c.autos, rotulo: 'autos lavrados', cmp: formataVariacao(cmp.autos) },
      {
        valor: c.lavrado, rotulo: 'valor lavrado', formata: formataReais, cmp: formataVariacao(cmp.lavrado),
        detalhe: `imposto ${formataReais(c.imposto)} · multas ${formataReais(c.multas)}`,
      },
      {
        valor: c.liquidado, rotulo: 'liquidado', formata: formataReais, cmp: formataVariacao(cmp.liquidado),
        detalhe: `${formataNumero(c.autos_liquidados)} autos`,
      },
      { valor: c.taxa_liquidacao, rotulo: 'taxa de liquidação (valor)', formata: pct, cmp: formataPontos(cmp.taxa_liquidacao) },
      { valor: c.valor_medio, rotulo: 'valor médio por auto', formata: formataReais, cmp: formataVariacao(cmp.valor_medio) },
    ];

    return (
      <>
        {dados.meses_incompletos > 0 && (
          <p className="small mb-2" style={{ color: 'var(--painel-alerta)' }}>
            <MDBIcon icon="exclamation-triangle" className="mr-1" />
            {dados.meses_incompletos === 1 ? '1 mês' : `${dados.meses_incompletos} meses`} do período ainda não
            {dados.meses_incompletos === 1 ? ' está completo' : ' estão completos'} na última carga da Receita
            (hachurados no gráfico): os totais e a comparação com {rotuloPeriodo(anterior)} os incluem assim mesmo.
          </p>
        )}
        <div className="row mb-3">
          {kpis.map((k) => (
            <div key={k.rotulo} className="col-lg col-md-4 col-sm-6 mb-2">
              <KpiTile
                valor={k.valor}
                rotulo={k.rotulo}
                formata={k.formata}
                detalhe={k.detalhe}
                comparacao={<span className="text-muted">{k.cmp} {vs}</span>}
                compacto
              />
            </div>
          ))}
        </div>

        <MDBCard className="mb-3"><MDBCardBody>
          <h6 className="mb-2">Por mês <small className="text-muted">(clique numa coluna para ver os autos do mês)</small></h6>
          <Legenda />
          <Colunas
            descricao="Valor lavrado e liquidado por mês"
            formata={formataReaisCurto}
            formataParte={formataReaisCurto}
            itens={dados.por_mes.map((m) => ({
              chave: m.mes,
              rotulo: rotuloMes(m.mes),
              total: m.lavrado,
              parte: m.liquidado,
              incompleto: m.incompleto,
              titulo: `${rotuloMes(m.mes)}: ${formataNumero(m.autos)} autos · lavrado ${formataReaisCheio(m.lavrado)}`
                + ` (imposto ${formataReais(m.imposto)} · multas ${formataReais(m.multas)})`
                + ` · liquidado ${formataReaisCheio(m.liquidado)}${m.incompleto ? ' · mês incompleto na carga' : ''}`,
            }))}
            aoClicar={(item) => irPara(linkLista(periodo, posto, { mes: item.chave }))}
          />
        </MDBCardBody></MDBCard>

        {/* Período maior que 12 meses: o backend não devolve por_ano — cada coluna
            cobriria mais de um ano e o mesmo mês contaria em dois. */}
        {dados.por_ano.length > 0 && (
          <MDBCard className="mb-3"><MDBCardBody>
            <h6 className="mb-2">
              Mesma janela em cada ano{' '}
              <small className="text-muted">({rotuloPeriodo(periodo).replace(/\/\d\d/g, '')} de cada ano)</small>
            </h6>
            <Legenda />
            <Colunas
              altura={120}
              descricao="Valor lavrado e liquidado na mesma janela de cada ano"
              formata={formataReaisCurto}
              formataParte={formataReaisCurto}
              itens={dados.por_ano.map((a, i, arr) => ({
                chave: String(a.ano),
                rotulo: rotuloJanela(a.ano, periodo),
                total: a.lavrado,
                parte: a.liquidado,
                destaque: i === arr.length - 1,
                titulo: `${rotuloJanela(a.ano, periodo)}: ${formataNumero(a.autos)} autos · lavrado ${formataReaisCheio(a.lavrado)} · liquidado ${formataReaisCheio(a.liquidado)}`,
              }))}
            />
          </MDBCardBody></MDBCard>
        )}

        <MDBRow className="mb-3">
          <MDBCol md="5" className="mb-2">
            <MDBCard className="h-100"><MDBCardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Situação dos autos</h6>
                {alterna('Ordenar situação', statusPorValor, setStatusPorValor)}
              </div>
              <ListaBarras
                topN={10}
                formata={statusPorValor ? formataReais : formataNumero}
                itens={dados.por_status.map((s) => ({
                  rotulo: s.status,
                  n: statusPorValor ? s.valor : s.autos,
                  outra: statusPorValor ? `${formataNumero(s.autos)} autos` : formataReais(s.valor),
                  // A lista não filtra status vazio: sem link, para não prometer um destino.
                  clicavel: s.status !== 'Sem acompanhamento',
                }))}
                destaque={(i) => i.outra || null}
                aoClicar={(i) => irPara(linkLista(periodo, posto, { status_acompanhamento: i.rotulo }))}
              />
            </MDBCardBody></MDBCard>
          </MDBCol>
          <MDBCol md="7" className="mb-2">
            <MDBCard className="h-100"><MDBCardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Infrações que mais pesam</h6>
                {alterna('Ordenar infrações', porValor, (v) => setOrdemInfracao(v ? 'por_valor' : 'por_autos'))}
              </div>
              <ListaBarras
                topN={10}
                formata={porValor ? formataReais : formataNumero}
                itens={infracoes.map((i) => ({
                  rotulo: rotuloInfracao(i),
                  clicavel: i.codigo != null,
                  n: porValor ? i.valor : i.autos,
                  codigo: i.codigo,
                  outra: porValor ? `${formataNumero(i.autos)} autos` : formataReais(i.valor),
                }))}
                destaque={(i) => i.outra || null}
                aoClicar={(i) => irPara(linkLista(periodo, posto, { nu_infracao: i.codigo }))}
              />
              {concentracao && <small className="text-muted d-block mt-2">{concentracao}</small>}
            </MDBCardBody></MDBCard>
          </MDBCol>
        </MDBRow>

        <MDBCard className="mb-3"><MDBCardBody>
          <h6 className="mb-3">Por posto <small className="text-muted">(do maior valor lavrado ao menor · abordagens e ocorrências: SGO)</small></h6>
          <div className="table-responsive">
            <table className="table table-sm mb-0" style={{ fontSize: '.9rem' }}>
              <thead>
                <tr>
                  <th>Posto</th>
                  <th className="text-right text-nowrap">Autos</th>
                  <th className="text-right text-nowrap">Lavrado</th>
                  <th className="text-right text-nowrap">Liquidado</th>
                  <th className="text-right text-nowrap">Taxa</th>
                  <th className="text-right text-nowrap">% do valor</th>
                  <th className="text-right text-nowrap">% dos autos</th>
                  <th className="text-right text-nowrap">Abordagens</th>
                  <th className="text-right text-nowrap">Ocorrências</th>
                  <th className="text-right text-nowrap">Autos / 100 abord.</th>
                </tr>
              </thead>
              <tbody>
                {ordenaPostos(dados.por_posto).map((p) => (
                  <tr key={p.posto}>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0 m-0 text-left"
                        style={linkSemCaixaAlta}
                        onClick={() => irPara(linkLista(periodo, posto, { posto: p.posto }))}
                      >
                        {p.posto}
                      </button>
                    </td>
                    <td className="text-right text-nowrap">{formataNumero(p.autos)}</td>
                    <td className="text-right text-nowrap" title={formataReaisCheio(p.lavrado)}>{formataReais(p.lavrado)}</td>
                    <td className="text-right text-nowrap" title={formataReaisCheio(p.liquidado)}>{formataReais(p.liquidado)}</td>
                    <td className="text-right text-nowrap">{pct(p.taxa_liquidacao)}</td>
                    <td className="text-right text-nowrap">{pct(p.participacao_valor)}</td>
                    <td className="text-right text-nowrap">{pct(p.participacao_autos)}</td>
                    <td className="text-right text-nowrap">{contagem(p.abordagens)}</td>
                    <td className="text-right text-nowrap">{contagem(p.ocorrencias)}</td>
                    <td
                      className="text-right text-nowrap"
                      title={p.sem_autos_receita ? 'Nenhum auto atribuído a este posto pela Receita no período' : undefined}
                    >
                      {p.autos_por_100_abordagens != null ? umaCasa(p.autos_por_100_abordagens) : '—'}
                      {p.sem_autos_receita ? '*' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              {dados.por_posto.length > 1 && (
                <tfoot>
                  <tr className="font-weight-bold">
                    <td>Total</td>
                    <td className="text-right text-nowrap">{formataNumero(totalPostos.autos)}</td>
                    <td className="text-right text-nowrap">{formataReais(totalPostos.lavrado)}</td>
                    <td className="text-right text-nowrap">{formataReais(totalPostos.liquidado)}</td>
                    <td className="text-right text-nowrap">{formataPct(totalPostos.liquidado, totalPostos.lavrado)}</td>
                    <td colSpan="2" />
                    <td className="text-right text-nowrap">{comSgo.length ? formataNumero(somaDe(comSgo, 'abordagens')) : '—'}</td>
                    <td className="text-right text-nowrap">{comSgo.length ? formataNumero(somaDe(comSgo, 'ocorrencias')) : '—'}</td>
                    <td
                      className="text-right text-nowrap"
                      title={comRazao.length ? `Só os postos com autos e abordagens: ${comRazao.map((p) => p.posto).join(', ')}` : undefined}
                    >
                      {abordRazao ? umaCasa((100 * somaDe(comRazao, 'autos')) / abordRazao) : '—'}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {notaSemAutosReceita && (
            <small className="text-muted d-block mt-2">* {notaSemAutosReceita}</small>
          )}
          {avisosSgo.map((a) => (
            <small key={a} className="d-block mt-1" style={{ color: 'var(--painel-alerta)' }}>
              <MDBIcon icon="exclamation-triangle" className="mr-1" />
              {a}
            </small>
          ))}
          {dados.sem_data > 0 && (
            <small className="text-muted d-block mt-2">
              {formataNumero(dados.sem_data)} autos sem data de lavratura ficam fora de qualquer período.
            </small>
          )}
        </MDBCardBody></MDBCard>
      </>
    );
  };

  return (
    <>
      <NavBar />
      <MDBContainer className="my-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-0">Autos de Infração</h4>
            {dados && (
              <small className="text-muted">
                carga da Receita de {dados.carga ? formataDataHora(dados.carga).slice(0, 10) : '—'}
                {' · '}
                {rotuloPeriodo(dados.periodo)}
                {dados.posto ? ` · ${dados.posto}` : ' · todos os postos'}
              </small>
            )}
          </div>
          <div className="painel-acoes">
            <NotaMetodologica itens={NOTA} />
            <button type="button" className="painel-acao" onClick={atualizar} disabled={carregando}>
              <MDBIcon icon="refresh" spin={carregando} />
              {carregando ? 'Atualizando…' : 'Atualizar'}
            </button>
          </div>
        </div>
        {filtrosForm}
        {corpo()}
      </MDBContainer>
    </>
  );
}
